import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    ListScentingLogParams,
    ScentingLogEntryInput,
} from '@/lib/api/scenting-log-api'
import { scentingLogApi } from '@/lib/api/scenting-log-api'
import { scentingLotKeys } from '@/hooks/use-scenting-lot-query'
import { sortingLotKeys } from '@/hooks/use-sorting-lot-query'
import { m } from '@/lib/paraglide/messages'

// ============================================================================
// Sổ lấy hương (#M-16)
// ============================================================================

export const scentingLogKeys = {
    all: ['scenting-log-entries'] as const,
    list: (params: ListScentingLogParams) =>
        [...scentingLogKeys.all, 'list', params] as const,
    detail: (id: string) => [...scentingLogKeys.all, 'detail', id] as const,
    divergence: (id: string) =>
        [...scentingLogKeys.all, 'divergence', id] as const,
    sourceClaims: (excludeEntryId?: string) =>
        [
            ...scentingLogKeys.all,
            'source-claims',
            excludeEntryId ?? '',
        ] as const,
}

export function useScentingLogEntries(params: ListScentingLogParams = {}) {
    return useQuery({
        queryKey: scentingLogKeys.list(params),
        queryFn: () => scentingLogApi.list(params),
    })
}

/** Một trang sổ = 20 mẻ, đủ phủ vài ngày làm việc mà vẫn tải nhanh trên máy tính bảng. */
export const SCENTING_LOG_PAGE_SIZE = 20

/**
 * Sổ tải theo trang: mở lên chỉ có trang đầu (mẻ mới nhất ở trên), bấm "Xem
 * thêm" thì nối trang kế tiếp xuống dưới chứ không thay trang.
 */
export function useScentingLogEntriesInfinite(
    params: Omit<ListScentingLogParams, 'page' | 'per_page'> = {},
) {
    return useInfiniteQuery({
        queryKey: [...scentingLogKeys.all, 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            scentingLogApi.list({
                ...params,
                page: pageParam,
                per_page: SCENTING_LOG_PAGE_SIZE,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useScentingLogEntry(id?: string) {
    return useQuery({
        queryKey: scentingLogKeys.detail(id ?? ''),
        queryFn: () => scentingLogApi.get(id as string),
        enabled: !!id,
    })
}

/**
 * M-140: phần từng lô nguồn đang bị các mẻ CHƯA VÀO KHO khác giữ. Ô chọn lô trừ
 * sẵn để số hiện ra khớp số được phép ghi lúc Lưu. Luôn đọc mới khi mở form:
 * phiếu có thể vừa được duyệt/từ chối ở màn khác.
 */
export function useScentingLogSourceClaims(excludeEntryId?: string) {
    return useQuery({
        queryKey: scentingLogKeys.sourceClaims(excludeEntryId),
        queryFn: () => scentingLogApi.sourceClaims(excludeEntryId),
        staleTime: 0,
        refetchOnMount: 'always',
    })
}

export function useScentingLogDivergence(id?: string, enabled = true) {
    return useQuery({
        queryKey: scentingLogKeys.divergence(id ?? ''),
        queryFn: () => scentingLogApi.divergence(id as string),
        enabled: !!id && enabled,
    })
}

export function useCreateScentingLogEntry() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: ScentingLogEntryInput) => scentingLogApi.create(req),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: scentingLogKeys.all })
            toast.success(m.scl_log_toast_saved())
        },
    })
}

export function useUpdateScentingLogEntry() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, req }: { id: string; req: ScentingLogEntryInput }) =>
            scentingLogApi.update(id, req),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: scentingLogKeys.all })
            // M-150: sửa sổ khi phiếu chờ duyệt thì phiếu đổi theo — bỏ bản
            // phiếu đang nhớ để mở phiếu ra thấy ngay số mới.
            void qc.invalidateQueries({ queryKey: scentingLotKeys.inNotes() })
            toast.success(m.scl_log_toast_updated())
        },
    })
}

export function useDeleteScentingLogEntry() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => scentingLogApi.remove(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: scentingLogKeys.all })
            toast.success(m.scl_log_toast_deleted())
        },
    })
}

export function useAdjustScentingLogEntry() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => scentingLogApi.adjust(id),
        onSuccess: (res) => {
            // Điều chỉnh vừa sinh phiếu ở CẢ HAI kho nên phải làm mới cả hai,
            // nếu không danh sách phiếu và tồn lô vẫn là số cũ.
            void qc.invalidateQueries({ queryKey: scentingLogKeys.all })
            void qc.invalidateQueries({ queryKey: scentingLotKeys.all })
            void qc.invalidateQueries({ queryKey: sortingLotKeys.all })
            toast.success(
                m.scl_log_toast_adjusted({ n: res.data?.length ?? 0 }),
            )
        },
    })
}
